import { useEffect } from 'react';
import ScrollReveal from 'scrollreveal';

const MOBILE_QUERY = '(max-width: 768px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const matches = (query) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches;

const DEFAULTS = {
  duration: 700,
  distance: '40px',
  easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Revelação de mão única. Com reset:true, uma geometria desatualizada (imagens e
  // fontes que só chegam DEPOIS do reveal) fazia o ScrollReveal concluir que o
  // elemento já saíra da tela e regravar opacity:0 inline — ele nunca mais voltava.
  // Era a causa do conteúdo sumir na primeira visita (com cache quente não havia
  // deslocamento de layout, por isso o F5 "resolvia").
  reset: false,
  // Só tem efeito quando reset é false: apaga os estilos inline ao fim da animação,
  // devolvendo transform/opacity ao CSS e destravando os efeitos de :hover.
  cleanup: true,
  mobile: true,
};

export const srPresets = {
  fadeUp: {
    ...DEFAULTS,
    origin: 'bottom',
    opacity: 0,
  },
  fadeDown: {
    ...DEFAULTS,
    origin: 'top',
    opacity: 0,
  },
  fadeLeft: {
    ...DEFAULTS,
    origin: 'right',
    distance: '60px',
    opacity: 0,
  },
  fadeRight: {
    ...DEFAULTS,
    origin: 'left',
    distance: '60px',
    opacity: 0,
  },
  zoomIn: {
    ...DEFAULTS,
    distance: '0px',
    scale: 0.88,
    opacity: 0,
  },
};

/**
 * @param {Array<{ selector: string, options?: object, interval?: number }>} reveals
 *
 * Passe sempre uma constante de módulo (definida fora do componente): a lista é lida
 * uma única vez, na montagem.
 *
 * Um elemento não pode ter mais de um dono de opacity/transform. Se um seletor já é
 * animado por `animation` no CSS ou recebe `.is-visible` de um IntersectionObserver,
 * ele NÃO deve ser passado aqui — os dois brigam e o conteúdo trava invisível.
 */
const useScrollReveal = (reveals) => {
  useEffect(() => {
    if (matches(MOBILE_QUERY) || matches(REDUCED_MOTION_QUERY)) {
      return undefined;
    }

    const sr = ScrollReveal();
    if (!sr || sr.noop) {
      return undefined;
    }

    reveals.forEach(({ selector, options = srPresets.fadeUp, interval }) => {
      try {
        if (interval) {
          sr.reveal(selector, { ...options, interval });
        } else {
          sr.reveal(selector, options);
        }
      } catch (_) {
        // selector not found — skip silently
      }
    });

    /**
     * O ScrollReveal só recalcula a geometria dos elementos nos eventos 'init' e
     * 'resize' (scrollreveal.js:1413) — rolagem nunca recalcula, e a biblioteca não
     * escuta load, fontes nem mudanças de tamanho. Como as posições são fotografadas
     * antes das imagens carregarem, qualquer deslocamento posterior deixava os
     * cálculos obsoletos. Um 'resize' sintético é o único gatilho que marca a
     * geometria como suja e força o recálculo de tudo.
     */
    let cancelled = false;
    let frame = null;

    const resync = () => {
      if (cancelled) return;
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!cancelled) window.dispatchEvent(new Event('resize'));
      });
    };

    // 1) imagens ainda em voo (cobre também navegação SPA, onde 'load' já passou)
    const pendingImages = Array.from(document.images).filter((img) => !img.complete);
    pendingImages.forEach((img) => {
      img.addEventListener('load', resync);
      img.addEventListener('error', resync);
    });

    // 2) carregamento completo da página
    if (document.readyState === 'complete') {
      resync();
    } else {
      window.addEventListener('load', resync);
    }

    // 3) troca da webfont, que remede o texto e desloca as seções
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(resync);
    }

    // 4) rede de segurança para qualquer deslocamento residual
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      let lastHeight = document.documentElement.scrollHeight;
      observer = new ResizeObserver(() => {
        const height = document.documentElement.scrollHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          resync();
        }
      });
      observer.observe(document.documentElement);
    }

    // 5) último recálculo tardio, para o que escapar dos gatilhos acima
    const safetyTimer = window.setTimeout(resync, 2500);

    return () => {
      // única forma de neutralizar o .then() pendente de document.fonts.ready
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      window.clearTimeout(safetyTimer);
      pendingImages.forEach((img) => {
        img.removeEventListener('load', resync);
        img.removeEventListener('error', resync);
      });
      window.removeEventListener('load', resync);
      if (observer) observer.disconnect();
      sr.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useScrollReveal;
